// Formatação — mesmas regras do plugchef.desktop e do bi.web, para parecer o mesmo produto.
//
// Aqui as datas vêm da API como instantes completos (ISO com hora e fuso: `timestamptz`), então
// `new Date(iso)` é o certo — diferente das datas "AAAA-MM-DD" do BI, que precisavam de `split`.

const DATA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const INTEIRO = new Intl.NumberFormat('pt-BR');

function valida(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function data(iso: string | null | undefined): string {
  const d = valida(iso);
  return d ? DATA.format(d) : '—';
}

export function dataHora(iso: string | null | undefined): string {
  const d = valida(iso);
  return d ? DATA_HORA.format(d) : '—';
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const DIA_MES = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

/** "14:05" (relógio de quem olha). */
export function hora(iso: string | null | undefined): string {
  const d = valida(iso);
  return d ? HORA.format(d) : '—';
}

/** "11/10" de um instante completo (para "vence em DD/MM"). */
export function diaMesDe(iso: string | null | undefined): string {
  const d = valida(iso);
  return d ? DIA_MES.format(d) : '—';
}

export function inteiro(v: number | null | undefined): string {
  return INTEIRO.format(Math.round(v ?? 0));
}

/**
 * "há 3 min", "há 2 h", "há 5 dias" — para a última conversa da loja.
 * `agora` vem de fora (useAgora) para o texto andar sozinho na tela, sem recarregar a lista.
 */
export function ha(iso: string | null | undefined, agora: number = Date.now()): string {
  const d = valida(iso);
  if (!d) return 'nunca';
  const seg = Math.round((agora - d.getTime()) / 1000);
  // Relógio da máquina um pouco adiantado/atrasado não pode virar "há -3 s".
  if (seg < 45) return 'agora há pouco';
  const min = Math.round(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias < 30) return `há ${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  return `em ${data(iso)}`;
}

/** "em 20 min", "em 3 h", "em 2 dias" — quanto falta (o espelho de `ha`). Já passou: "já passou". */
export function emQuanto(iso: string | null | undefined, agora: number = Date.now()): string {
  const d = valida(iso);
  if (!d) return '—';
  const seg = Math.round((d.getTime() - agora) / 1000);
  if (seg <= 0) return 'já passou';
  const min = Math.max(1, Math.round(seg / 60));
  if (min < 60) return `em ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `em ${h} h`;
  const dias = Math.floor(h / 24);
  return dias < 30 ? `em ${dias} ${dias === 1 ? 'dia' : 'dias'}` : `em ${data(iso)}`;
}

/** Dias inteiros até `iso` (arredondado para cima, como a API: 2 h restantes ainda é "1 dia"). */
export function diasAte(iso: string | null | undefined, agora: number = Date.now()): number | null {
  const d = valida(iso);
  if (!d) return null;
  const restante = d.getTime() - agora;
  return restante > 0 ? Math.ceil(restante / 86_400_000) : 0;
}

export function plural(n: number, singular: string, pluralTexto: string): string {
  return `${n} ${n === 1 ? singular : pluralTexto}`;
}

/** CNPJ 00.000.000/0000-00 ou CPF 000.000.000-00 (a API guarda só os dígitos). */
export function documento(doc: string | null | undefined): string {
  const d = (doc ?? '').replace(/\D/g, '');
  if (d.length === 14) return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (d.length === 11) return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return doc || '—';
}

/** (44) 99999-9999 / (44) 3333-3333 — o que não tiver 10 ou 11 dígitos sai como veio. */
export function telefone(tel: string | null | undefined): string {
  const d = (tel ?? '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return tel || '—';
}

// ─── Cobrança (F2) ──────────────────────────────────────────────────────────────────────────────

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const DECIMAL = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** R$ 1.234,56 (`—` sem valor). */
export function moeda(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : MOEDA.format(v);
}

const MOEDA_FINA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 4 });
const QUANTIDADE = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 5 });

/**
 * R$ 0,0035 — custo unitário de insumo (por grama, por ml), que com 2 casas viraria R$ 0,00. Até 4
 * casas, nunca menos de 2.
 */
export function moedaFina(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : MOEDA_FINA.format(v);
}

/** Quantidade do catálogo (até 5 casas, sem zeros à direita): 0,150 → "0,15". */
export function quantidade(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : QUANTIDADE.format(v);
}

/** 1.234,56 — para preencher um campo de valor. */
export function decimal(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '' : DECIMAL.format(v);
}

/**
 * Lê um valor digitado ("1.234,56", "249,9", "249.90") como número. `null` se não for número.
 * Ponto seguido de exatamente 3 dígitos é milhar; senão é a vírgula de quem digitou no teclado inglês.
 */
export function lerDecimal(texto: string): number | null {
  let t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * Data de CALENDÁRIO 'AAAA-MM-DD' (vencimento, fidelidade) em DD/MM/AAAA, SEM passar por `Date`:
 * `new Date('2026-10-10')` é meia-noite UTC, e no Brasil isso cai no dia 9. Um instante completo
 * (com hora) cai no `data()` de sempre.
 */
export function dataDia(dia: string | null | undefined): string {
  if (!dia) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : data(dia);
}

/** 'AAAA-MM-DD' em DD/MM (textos curtos: "de 02/10 a 10/11"). */
export function diaMes(dia: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dia ?? '');
  return m ? `${m[3]}/${m[2]}` : dataDia(dia);
}

/**
 * Hoje no calendário de Brasília (UTC−3, sem horário de verão desde 2019), como a API calcula.
 * Só para GUIAR a tela (ex.: a fidelidade já venceu?) — quem decide é sempre o servidor.
 */
export function hojeBrasil(agora: number = Date.now()): string {
  return new Date(agora - 3 * 3_600_000).toISOString().slice(0, 10);
}

/** 'AAAA-MM-DD' + n dias, no calendário (em UTC, sem fuso: a mesma conta do `somarDias` da API). */
export function somarDiasDia(dia: string, dias: number): string {
  const [a, m, d] = dia.split('-').map(Number);
  return new Date(Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1) + dias * 86_400_000).toISOString().slice(0, 10);
}

// ─── PlugAI ─────────────────────────────────────────────────────────────────────────────────────

const CREDITOS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });
const CREDITOS_FINOS = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 });

/**
 * Créditos do PlugAI (saldo, consumo, extrato): até 2 casas, sem zeros à direita — 1.234,5 / 0,07. A
 * carteira guarda 4 casas; a tela arredonda (`—` sem valor).
 */
export function creditos(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : CREDITOS.format(v);
}

/**
 * Preço do motor (créditos por 1.000 tokens, por busca): até 4 casas. Uma tarifa de 0,0025 com 2 casas
 * viraria "0" na tabela — e é a conta da loja e da nuvem (`creditosDaChamada`, 4 casas).
 */
export function creditosFinos(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? '—' : CREDITOS_FINOS.format(v);
}

/** 10 → "10%", 12.5 → "12,5%". */
export function percentual(v: number): string {
  return `${INTEIRO.format(Math.round(v * 10) / 10)}%`;
}

/** Segredo do autenticador em blocos de 4, para digitar à mão sem se perder. */
export function emBlocos(texto: string, tamanho = 4): string {
  return (texto.replace(/\s+/g, '').match(new RegExp(`.{1,${tamanho}}`, 'g')) ?? []).join(' ');
}

/** Desvio do relógio da loja em texto ("adiantado 3 min"). `null` = desprezível (até 1 min). */
export function desvioRelogio(seg: number | null | undefined): string | null {
  if (seg == null || Math.abs(seg) <= 60) return null;
  const min = Math.round(Math.abs(seg) / 60);
  const quanto = min >= 120 ? `${Math.round(min / 60)} h` : `${min} min`;
  return `${seg > 0 ? 'adiantado' : 'atrasado'} ${quanto}`;
}

/** "320 KB", "12,4 MB", "1,2 GB" (o tamanho de um pacote ou de um backup). */
export function tamanhoArquivo(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} GB`;
}
